import { useCallback, useState } from 'react';

export type ValidationErrorsType = Record<string, string>;

interface UseAuthFormValidationOptions<T extends Record<string, unknown> | object> {
    initialState: T;
    validateForm: (formData: T) => Record<string, string>;
}

export function useAuthFormValidation<T extends Record<string, unknown> | object>({
    initialState,
    validateForm: validateFormFn
}: UseAuthFormValidationOptions<T>) {
    const [formData, setFormData] = useState<T>(initialState);
    const [validationErrors, setValidationErrors] = useState<ValidationErrorsType>({});

    const handleInputChange = useCallback((field: keyof T, value: string | boolean) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));

        setValidationErrors(prev => {
            if (prev[field as string]) {
                const newErrors = { ...prev };
                delete newErrors[field as string];
                return newErrors;
            }
            return prev;
        });
    }, []);

    const validateFormData = useCallback((): boolean => {
        const errors = validateFormFn(formData);
        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    }, [formData, validateFormFn]);

    const getFieldError = useCallback((fieldName: string): string => {
        return validationErrors[fieldName] || '';
    }, [validationErrors]);

    const resetForm = useCallback(() => {
        setFormData(initialState);
        setValidationErrors({});
    }, [initialState]);

    return {
        formData,
        setFormData,
        validationErrors,
        setValidationErrors,
        handleInputChange,
        validateForm: validateFormData,
        getFieldError,
        resetForm,
    };
} 